import classNames from 'classnames';
import { stringify } from 'qs';
import { useCallback, useMemo } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { Container, Heading } from 'yoso';
import { LoginSecurity } from '~/components/Auth/Login/Security/LoginSecurity';
import { useChallengeEventHistories } from '~/components/Challenge/Event/ChallengeEvent.hooks';
import { ChallengeParticipatedEventCelect } from '~/components/Challenge/Event/Dropdown/Participated/ChallengeParticipatedEventCelect';
import { ChallengeHeader } from '~/components/Challenge/Header/ChallengeHeader';
import {
  useChallengeEventParams,
  useChallengeRewards,
} from '~/components/Challenge/Purchase/ChallengePurchase.hooks';
import styles from '~/components/Challenge/Purchase/ChallengePurchasePage.module.scss';
import { ChallengePurchaseErrorFallback } from '~/components/Challenge/Purchase/Error/ChallengePurchaseErrorFallback';
import { ChallengePurchaseList } from '~/components/Challenge/Purchase/List/ChallengePurchaseList';
import { ChallengePurchaseListErrorFallback } from '~/components/Challenge/Purchase/List/Error/ChallengePurchaseListErrorFallback';
import summaryStyles from '~/components/Challenge/Summary/ChallengeSummary.module.scss';
import { currencySymbol } from '~/components/Currency/Currency.hooks';
import { QueryErrorBoundary } from '~/components/Error';
import { CelectProps } from '~/components/Form/Celect';
import { T } from '~/components/I18n';
import { QuestionV2 } from '~/components/Icon/QuestionV2';
import { Page, PageProps } from '~/components/Page';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { format } from '~/components/Util/number';
export type ChallengePurchasePageProps = PageProps & RouteComponentProps;

export const ChallengePurchasePage = ({ className }: ChallengePurchasePageProps) => {
  const params = useChallengeEventParams();
  const { data, isFetched } = useChallengeEventHistories();
  const latestId = useMemo(
    () => data?.pages.flatMap(({ histories }) => histories).find(({ week }) => !!week)?.week,
    [data]
  );
  const eventId: number | undefined = params.eventId || latestId;

  const {
    data: { totalPurchaseAmountInUsd, purchaseRewardScore },
    isInitialLoading,
  } = useChallengeRewards(eventId, { enabled: !!eventId });

  const history = useHistory();
  const onChange: CelectProps['onChange'] = useCallback(
    (value) => {
      history.replace(
        `/challenge/purchase${stringify({ eventId: value }, { addQueryPrefix: true })}`
      );
    },
    [history]
  );

  return (
    <Page
      className={classNames(styles.root, { [styles.loading]: !isFetched }, className)}
      data-event-page_name="challenge_history_score"
      data-event-category="challenge_history">
      <Container margin="auto">
        <ChallengeHeader />
        <div className={styles.body}>
          <QueryErrorBoundary FallbackComponent={ChallengePurchaseErrorFallback}>
            <div className={classNames(styles.summary, summaryStyles.root)}>
              <Heading level={4} className={summaryStyles.heading}>
                <ChallengeParticipatedEventCelect
                  value={eventId?.toString()}
                  onChange={onChange}
                  placement="bottom-start"
                  className={summaryStyles.dropdown}
                />
                <br />
                <T name="citizen_challenge_history_purchasescore_title" />{' '}
                <T
                  name="citizen_unit_score"
                  className={summaryStyles.highlight}
                  options={[format(purchaseRewardScore, { zeroAsEmpty: isInitialLoading })]}
                />
              </Heading>
              <div className={summaryStyles.description}>
                <T name="citizen_challenge_history_amount" options={[eventId]} />
                {currencySymbol['USD']}
                {format(totalPurchaseAmountInUsd, { zeroAsEmpty: isInitialLoading })}
                <Tooltip
                  className={summaryStyles.tooltip}
                  variant="secondary"
                  direction="bottom"
                  tooltipText={<T name="citizen_challenge_history_purchasescore_tooltip" />}>
                  <QuestionV2 className={summaryStyles.icon} />
                </Tooltip>
              </div>
            </div>
            <LoginSecurity>
              <QueryErrorBoundary
                FallbackComponent={ChallengePurchaseListErrorFallback}
                resetKeys={[eventId]}>
                <ChallengePurchaseList eventId={eventId} />
              </QueryErrorBoundary>
            </LoginSecurity>
          </QueryErrorBoundary>
        </div>
      </Container>
    </Page>
  );
};
